/**
 * PatentsPage -- CRUD for patents in the content library.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useContentStore, useContentActions } from '../../state/cms/useContentStore';
import { Panel } from '../../components/ui/Panel';
import { Input } from '../../components/ui/Input';
import { Textarea } from '../../components/ui/Textarea';
import { MonthYearPicker } from '../../components/ui/MonthYearPicker';
import { Button } from '../../components/ui/Button';
import { LoadingState } from '../../components/ui/LoadingState';
import { useSaveHandler } from '../../hooks/useSaveHandler';
import { logger } from '../../services/logger.client';
import type { Patent } from '../../types/cms.types';

const log = logger.child({ module: 'PatentsPage' });

/* ------------------------------------------------------------------ */
/*  Patent Editor                                                     */
/* ------------------------------------------------------------------ */

interface EditorProps {
  entry: Patent;
}

function PatentEditor({ entry }: EditorProps) {
  const { updatePatent, deletePatent } = useContentActions();

  const [title, setTitle] = useState(entry.title);
  const [patentNumber, setPatentNumber] = useState(entry.patent_number ?? '');
  const [issueDate, setIssueDate] = useState(entry.issue_date ?? '');
  const [inventors, setInventors] = useState((entry.inventors ?? []).join(', '));
  const [description, setDescription] = useState(entry.base_description ?? '');
  const { isSaving, saveMessage, saveError, execute: executeSave } = useSaveHandler({
    getStoreError: () => useContentStore.getState().error,
  });

  async function handleSave() {
    await executeSave(() =>
      updatePatent(entry.id, {
        title,
        patent_number: patentNumber || null,
        issue_date: issueDate || null,
        inventors: inventors
          .split(',')
          .map((i) => i.trim())
          .filter(Boolean),
        base_description: description || null,
      }),
    );
  }

  async function handleDelete() {
    if (!window.confirm('Delete this patent?')) return;
    await deletePatent(entry.id);
  }

  return (
    <Panel>
      <Panel.Header>{entry.title}</Panel.Header>
      <Panel.Content>
        <div className="editor-field-group">
          <Input
            label="Patent Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <div className="experience-entry__row">
            <Input
              label="Patent Number"
              value={patentNumber}
              onChange={(e) => setPatentNumber(e.target.value)}
              placeholder="US-12345678"
            />
            <MonthYearPicker
              label="Issue Date"
              value={issueDate}
              onChange={setIssueDate}
              placeholder="Select issue month"
            />
          </div>
          <Input
            label="Inventors (comma-separated)"
            value={inventors}
            onChange={(e) => setInventors(e.target.value)}
            placeholder="John Doe, Jane Smith"
          />
          <Textarea
            label="Description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Brief description of the patent..."
          />
          <div className="cms-page__actions-row">
            {saveError && (
              <span className="editor-page__save-msg editor-page__save-msg--error">
                {saveError}
              </span>
            )}
            {saveMessage && (
              <span className="editor-page__save-msg">{saveMessage}</span>
            )}
            <Button variant="danger" size="sm" onClick={() => void handleDelete()}>
              Delete
            </Button>
            <Button variant="primary" size="sm" onClick={() => void handleSave()} loading={isSaving}>
              Save
            </Button>
          </div>
        </div>
      </Panel.Content>
    </Panel>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                              */
/* ------------------------------------------------------------------ */

export function PatentsPage() {
  const patents = useContentStore((s) => s.patents);
  const isLoading = useContentStore((s) => s.isLoading);
  const error = useContentStore((s) => s.error);
  const { fetchPatents, createPatent } = useContentActions();

  const [showCreate, setShowCreate] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  useEffect(() => {
    log.info('PatentsPage mounted');
    void fetchPatents();
  }, [fetchPatents]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!newTitle.trim()) return;

    await createPatent({
      title: newTitle.trim(),
      display_order: patents.length,
    });

    setNewTitle('');
    setShowCreate(false);
  }

  return (
    <div className="cms-page">
      <header className="cms-page__header">
        <h1 className="cms-page__title">Patents</h1>
        <Button
          variant="primary"
          size="md"
          onClick={() => setShowCreate(!showCreate)}
        >
          + Add Patent
        </Button>
      </header>

      {showCreate && (
        <Panel>
          <Panel.Header>New Patent</Panel.Header>
          <Panel.Content>
            <form className="editor-field-group" onSubmit={handleCreate} noValidate>
              <Input
                label="Patent Title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder="Method and system for..."
                required
              />
              <div className="cms-page__actions-row">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCreate(false)}
                  type="button"
                >
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  Create
                </Button>
              </div>
            </form>
          </Panel.Content>
        </Panel>
      )}

      <LoadingState loading={isLoading} error={error}>
        {patents.length === 0 ? (
          <div className="list-empty">
            No patents yet. Click '+ Add Patent' to get started.
          </div>
        ) : (
          <div className="cms-page__body">
            {patents.map((patent) => (
              <PatentEditor key={patent.id} entry={patent} />
            ))}
          </div>
        )}
      </LoadingState>
    </div>
  );
}
